import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { updateLocation, generateReference, detectLocations, createLocation } from "@/services/locationService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { IconLocations } from "@/components/Icons";
import { Wand2, Loader2, MapPin, Plus, Save, X, Camera, Pencil, Building, Mountain, ChevronLeft, ChevronRight, Maximize2, RefreshCw, Search, Download, Layers } from "lucide-react";
import Location3DDesigner from "@/components/Location3DDesigner";

function LocationThumb({ location, isSelected, onClick }) {
  const hasImage = location.reference_image;
  const isExterior = location.location_type === 'EXT';
  
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="flex-shrink-0 relative group"
      data-testid={`loc-thumb-${location.id}`}
    >
      <div className={`w-20 h-14 rounded-xl overflow-hidden border-2 transition-all ${
        isSelected 
          ? 'border-[var(--color-gold-500)] ring-2 ring-[var(--color-gold-500)]/30' 
          : 'border-transparent hover:border-[var(--border-strong)]'
      }`}>
        {hasImage ? (
          <img src={`data:image/png;base64,${location.reference_image}`} alt={location.name} className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${
            isExterior ? 'bg-gradient-to-br from-sky-900/40 to-emerald-900/30' : 'bg-gradient-to-br from-amber-900/40 to-stone-900/30'
          }`}>
            {isExterior ? <Mountain size={16} className="text-[var(--text-disabled)]" /> : <Building size={16} className="text-[var(--text-disabled)]" />}
          </div>
        )}
      </div>
      <p className={`text-[10px] mt-1.5 text-center truncate w-20 ${
        isSelected ? 'text-[var(--color-gold-400)] font-medium' : 'text-[var(--text-muted)]'
      }`}>
        {location.name}
      </p>
      {!hasImage && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--color-warning)] rounded-full border-2 border-[var(--bg-surface)]" />
      )}
    </motion.button>
  );
}

// Large Image Preview with Lightbox
function ImagePreview({ src, alt, label, onRegenerate, isGenerating }) {
  const [showLightbox, setShowLightbox] = useState(false);
  
  return (
    <>
      <div className="relative group">
        <div className="rounded-2xl overflow-hidden bg-[var(--bg-muted)] border border-[var(--border-subtle)]">
          {src ? (
            <img 
              src={src} 
              alt={alt} 
              className="w-full h-auto"
              style={{ maxHeight: '60vh' }}
            />
          ) : (
            <div className="aspect-video flex flex-col items-center justify-center">
              <MapPin size={48} className="text-[var(--text-disabled)] mb-3" />
              <p className="text-sm text-[var(--text-muted)]">Not generated</p>
            </div>
          )}
        </div>
        
        {/* Label - positioned below image */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-medium text-[var(--color-gold-400)]">{label}</span>
          <div className="flex gap-2">
            {src && (
              <button
                onClick={() => setShowLightbox(true)}
                className="btn-ghost btn-icon-xs"
                title="View full size"
              >
                <Maximize2 size={14} />
              </button>
            )}
            <Button
              onClick={onRegenerate}
              disabled={isGenerating}
              size="sm"
              className="btn-ghost text-xs"
            >
              {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="ml-1">Regenerate</span>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Lightbox */}
      <AnimatePresence>
        {showLightbox && src && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <button 
              className="absolute top-6 right-6 btn-ghost btn-icon text-white hover:bg-white/10"
              onClick={() => setShowLightbox(false)}
            >
              <X size={24} />
            </button>
            <img 
              src={src} 
              alt={alt} 
              className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
              {label} • Click outside or X to close
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Full Location Detail View
function LocationDetailView({ location, onUpdate, onClose, onPrev, onNext, hasPrev, hasNext }) {
  const [name, setName] = useState(location?.name || "");
  const [description, setDescription] = useState(location?.description || "");
  const [locationType, setLocationType] = useState(location?.location_type || "INT");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState({ photo: false, sketch: false });
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (location) {
      setName(location.name || "");
      setDescription(location.description || "");
      setLocationType(location.location_type || "INT");
    }
  }, [location]);

  if (!location) return null;

  const isExterior = locationType === 'EXT';

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLocation(location.id, {
        name, description, location_type: locationType
      });
      toast.success("Saved");
      onUpdate();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (style = "photorealistic") => {
    if (!description.trim()) {
      toast.error("Please add a description first");
      return;
    }
    
    const styleKey = style === "photorealistic" ? "photo" : "sketch";
    setGenerating(prev => ({ ...prev, [styleKey]: true }));
    try {
      await updateLocation(location.id, {
        name, description, location_type: locationType
      });
      await generateReference(location.id, { style });
      toast.success("Reference generated!");
      onUpdate();
    } catch (error) {
      toast.error("Generation failed - please try again");
    } finally {
      setGenerating(prev => ({ ...prev, [styleKey]: false }));
    }
  };

  const DETAIL_TABS = [
    { id: "details", label: "Details" },
    { id: "3d-design", label: "3D Design" },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="h-14 px-6 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <button onClick={onPrev} disabled={!hasPrev} className="btn-ghost btn-icon-sm disabled:opacity-30" data-testid="prev-location">
              <ChevronLeft size={20} />
            </button>
            <button onClick={onNext} disabled={!hasNext} className="btn-ghost btn-icon-sm disabled:opacity-30" data-testid="next-location">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className={`chip ${isExterior ? 'chip-teal' : 'chip-gold'}`}>
              {isExterior ? 'EXT' : 'INT'}
            </span>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">{location.name}</h2>
          </div>
          {/* Tab Switcher */}
          <div className="flex items-center bg-[var(--bg-elevated)] rounded-lg p-0.5 ml-4">
            {DETAIL_TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                  activeTab === t.id
                    ? "bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)] font-medium"
                    : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
                data-testid={`location-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="btn-ghost btn-icon" data-testid="close-location-view">
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      {activeTab === "details" ? (
        /* Details Tab — original content */
        <div className="flex-1 overflow-hidden flex">
          {/* Left Side - Image */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-2xl">
              <ImagePreview
                src={location.reference_image ? `data:image/png;base64,${location.reference_image}` : null}
                alt={location.name}
                label="Reference Image"
                onRegenerate={() => handleGenerate("photorealistic")}
                isGenerating={generating.photo}
              />
            </div>
          </div>

          {/* Right Side - Details & Actions */}
          <div className="w-96 border-l border-[var(--border-subtle)] bg-[var(--bg-surface)] flex flex-col flex-shrink-0">
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              <div>
                <label className="label mb-2 block">Location Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="input" data-testid="location-name" />
              </div>
              <div>
                <label className="label mb-2 block">Type</label>
                <div className="flex gap-2">
                  {[
                    { id: "INT", label: "Interior", icon: Building },
                    { id: "EXT", label: "Exterior", icon: Mountain }
                  ].map((type) => {
                    const Icon = type.icon;
                    const isActive = locationType === type.id;
                    return (
                      <button key={type.id} onClick={() => setLocationType(type.id)}
                        className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                          isActive 
                            ? 'bg-[var(--color-gold-500)] text-[var(--color-base-950)] shadow-lg' 
                            : 'bg-[var(--bg-overlay)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] border border-[var(--border-subtle)]'
                        }`}
                        style={isActive ? { boxShadow: 'var(--glow-gold)' } : {}}
                        data-testid={`type-${type.id}`}
                      >
                        <Icon size={16} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="label mb-2 block">Visual Description</label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the location's visual style, atmosphere, lighting, architectural style..."
                  className="input resize-none h-32" data-testid="location-description" />
                <p className="text-xs text-[var(--text-disabled)] mt-2">Detailed descriptions produce better reference images</p>
              </div>
              <div className="space-y-3">
                <label className="label mb-2 block">Generate Reference</label>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => handleGenerate("photorealistic")} disabled={generating.photo || !description.trim()}
                    className="btn-primary justify-center" data-testid="generate-photo-btn">
                    {generating.photo ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                    <span className="ml-2">Photo</span>
                  </Button>
                  <Button onClick={() => handleGenerate("sketch")} disabled={generating.sketch || !description.trim()}
                    className="btn-secondary justify-center" data-testid="generate-sketch-btn">
                    {generating.sketch ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={16} />}
                    <span className="ml-2">Sketch</span>
                  </Button>
                </div>
              </div>
              <div className="divider" />
              <Button onClick={handleSave} disabled={saving}
                className="btn-ghost w-full justify-center border border-[var(--border-default)]"
                data-testid="save-location-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                <span className="ml-2">Save Changes</span>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* 3D Design Tab */
        <div className="flex-1 overflow-hidden">
          <Location3DDesigner location={location} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}

// Main Locations Page
export default function LocationsPage({ projectId, locations, onUpdate }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedLocation = selectedIndex !== null ? locations[selectedIndex] : null;

  const filteredLocations = locations.filter(l =>
    l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const data = await detectLocations(projectId);
      toast.success(data.message);
      onUpdate();
    } catch (error) {
      toast.error("Detection failed");
    } finally {
      setDetecting(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createLocation({
        project_id: projectId,
        name: newName,
        description: ""
      });
      toast.success("Location created");
      setNewName("");
      setShowCreateDialog(false);
      onUpdate();
    } catch (error) {
      toast.error("Failed to create");
    }
  };

  // Update selected when list changes
  useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= locations.length) {
      setSelectedIndex(locations.length > 0 ? locations.length - 1 : null);
    }
  }, [locations, selectedIndex]);

  // No locations - show empty state
  if (locations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="empty-state-icon mx-auto mb-8">
            <IconLocations size={48} />
          </div>
          <h2 className="h2 text-[var(--text-primary)] mb-4">No Locations Yet</h2>
          <p className="text-[var(--text-muted)] mb-8 leading-relaxed">
            Add locations manually or detect them automatically from your script's scene headings.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleDetect} disabled={detecting} className="btn-secondary">
              {detecting ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              <span className="ml-2">Detect from Script</span>
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="btn-primary">
              <Plus size={16} />
              <span className="ml-2">Add Location</span>
            </Button>
          </div>
        </div>
        
        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-gold-500)]/10 flex items-center justify-center">
                  <MapPin size={20} className="text-[var(--color-gold-400)]" />
                </div>
                Add Location
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Location name (e.g., Coffee Shop)"
                className="input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                data-testid="new-location-name"
              />
              <Button onClick={handleCreate} disabled={!newName.trim()} className="btn-primary w-full" data-testid="create-location-btn">
                Create Location
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Location Selector Strip with Search */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex-shrink-0">
        <div className="px-6 pt-4 pb-2 flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none z-10" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search locations..."
              className="input h-8 text-xs"
              style={{ paddingLeft: '2.25rem' }}
              data-testid="search-locations"
            />
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <Button onClick={handleDetect} disabled={detecting} className="btn-ghost h-8 w-8 p-0" size="sm" data-testid="detect-locations-btn">
              {detecting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="btn-primary h-8" size="sm" data-testid="add-location-btn">
              <Plus size={14} />
              <span className="ml-1 text-xs">Add</span>
            </Button>
          </div>
        </div>
        <div className="h-20 px-6 flex items-center">
          <div className="flex-1 overflow-x-auto scrollbar-thin">
            <div className="flex gap-4 py-2 min-w-0">
              {filteredLocations.map((loc) => {
                const idx = locations.indexOf(loc);
                return (
                  <LocationThumb
                    key={loc.id}
                    location={loc}
                    isSelected={selectedIndex === idx}
                    onClick={() => setSelectedIndex(idx)}
                  />
                );
              })}
              {filteredLocations.length === 0 && searchQuery && (
                <p className="text-xs text-[var(--text-muted)] py-4">No locations match "{searchQuery}"</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          {selectedLocation ? (
            <motion.div
              key={selectedLocation.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="h-full"
            >
              <LocationDetailView
                location={selectedLocation}
                onUpdate={onUpdate}
                onClose={() => setSelectedIndex(null)}
                onPrev={() => setSelectedIndex(prev => Math.max(0, prev - 1))}
                onNext={() => setSelectedIndex(prev => Math.min(locations.length - 1, prev + 1))}
                hasPrev={selectedIndex > 0}
                hasNext={selectedIndex < locations.length - 1}
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="h-full flex items-center justify-center"
            >
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-[var(--bg-overlay)] flex items-center justify-center mx-auto mb-6 border border-[var(--border-default)]">
                  <MapPin size={36} className="text-[var(--text-disabled)]" />
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-2">Select a Location</h3>
                <p className="text-sm text-[var(--text-muted)] max-w-sm">
                  Click on a location above to view and edit details, or generate reference images.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[var(--color-gold-500)]/10 flex items-center justify-center">
                <MapPin size={20} className="text-[var(--color-gold-400)]" />
              </div>
              Add Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Location name (e.g., Coffee Shop)"
              className="input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              data-testid="new-location-name"
            />
            <Button onClick={handleCreate} disabled={!newName.trim()} className="btn-primary w-full" data-testid="create-location-btn">
              Create Location
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
